import { Landmark, Trash2 } from "lucide-react";
import { useId } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import type { DeleteEntityVariant } from "./DeleteEntityDialogs";

export interface EntityDangerZoneProps {
  /** Entity the destructive action targets — decides the copy, same contract as `DeleteEntityDialogs`. */
  variant: DeleteEntityVariant;
  /** Name of the entity the action targets — makes the blast radius explicit before the dialogs open. */
  entityName?: string | null;
  onAction: () => void;
  disabled?: boolean;
  /**
   * Why the action cannot run right now. When set, the action is refused and this replaces the entity name.
   * The band also leaves its danger tone: nothing destructive is pending, and red would read as an error the
   * user caused rather than a state they have to clear first.
   */
  blockedReason?: string | null;
  /** The way forward offered beside the refusal — e.g. open the client's bank accounts. */
  blockedAction?: { label: string; onClick: () => void };
}

/** Full, entity-scoped action name: the card headline and the button's accessible name. */
const HEADING_KEYS = {
  client: "view_btn_delete_client",
  organization: "view_btn_delete_organization",
} as const;

/**
 * Destructive-action band shared by the client detail drawer and the organization detail page, so both read
 * the same. It only announces the action — the two-step confirmation lives in `DeleteEntityDialogs`.
 *
 * Copy is resolved from `variant` instead of being passed in, so the headline and the button cannot drift:
 * both call sites used to hand over the same string and rendered the entity-scoped label twice. The button
 * now carries the short verb, and its `aria-label` keeps the full name for screen readers.
 *
 * The headline stays put across both states so the band is recognisable at a glance; only the subline, the
 * tone and the controls change.
 */
export function EntityDangerZone({
  variant,
  entityName,
  onAction,
  disabled = false,
  blockedReason = null,
  blockedAction,
}: EntityDangerZoneProps) {
  const { t } = useTranslations("clients");
  const heading = t(HEADING_KEYS[variant]);
  const reasonId = useId();
  const blocked = Boolean(blockedReason);

  return (
    <div
      className={cn(
        "flex flex-wrap items-center justify-between gap-3 rounded-xl border px-4 py-3",
        blocked ? "border-border bg-muted" : "border-danger/30 bg-danger-container",
      )}
    >
      {/* A real basis, not `flex-1`'s zero: with none the row never wraps, and in a narrow drawer the buttons
          kept their width and squeezed the reason down to nothing. Now they drop under the text instead. */}
      <div className="flex min-w-0 flex-1 basis-64 items-center gap-3">
        <span
          className={cn(
            "inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-full",
            blocked ? "bg-background text-muted-foreground" : "bg-danger/10 text-danger",
          )}
        >
          {/* The icon names what the band is about: the blocker when refused, the action otherwise. */}
          {blocked ? <Landmark className="h-4 w-4" /> : <Trash2 className="h-4 w-4" />}
        </span>
        <div className="min-w-0">
          <p
            className={cn(
              "text-sm font-semibold",
              blocked ? "text-foreground" : "text-danger-container-foreground",
            )}
          >
            {heading}
          </p>
          {blocked ? (
            <p id={reasonId} className="text-xs leading-relaxed text-muted-foreground">
              {blockedReason}
            </p>
          ) : (
            entityName && <p className="truncate text-xs text-danger-container-foreground/80">{entityName}</p>
          )}
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        {blockedAction && (
          <ColocaButton
            type="button"
            variant="secondary"
            onClick={blockedAction.onClick}
            disabled={disabled}
            icon={Landmark}
          >
            {blockedAction.label}
          </ColocaButton>
        )}
        {/* Kept visible while blocked, not removed: the operator came here to delete, and a button that
            vanished would leave them looking for it instead of reading why it is unavailable. */}
        <ColocaButton
          type="button"
          variant="destructive"
          onClick={onAction}
          disabled={disabled || blocked}
          icon={Trash2}
          aria-label={heading}
          aria-describedby={blocked ? reasonId : undefined}
        >
          {t("view_danger_zone_action_delete")}
        </ColocaButton>
      </div>
    </div>
  );
}
